import { styled } from '@volkswagen-onehub/components-core';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
	getFinanciacionType,
	getPagoFinanciado,
	getPagoUnico,
} from '../helpers/ofertas-helper';
import {
	CarlineWithOfertas,
	FinanceData,
	Oferta,
	Promocion,
} from '../services/ofertas-service/models/ofertas';
import { useTrackingManager } from '../tracking/use-tracking-manager';
import { OfertaCard } from './oferta-card/oferta-card';

const OfertasContainer = styled.div<{
	isMobile: boolean;
	hasOneCarline: boolean;
}>`
	position:  ${(props) => props.isMobile && !props.hasOneCarline && 'absolute'};
	top: ${(props) => props.isMobile && !props.hasOneCarline && '100px'};
	left: ${(props) => props.isMobile && '0'};
	
	display: flex;
	flex-direction: ${(props) => (props.isMobile ? 'row' : 'column')}};
	gap: ${(props) => props.theme.size.static200};
	
	overflow-x: ${(props) => props.isMobile && 'scroll'};
	scroll-snap-type: ${(props) => props.isMobile && !props.hasOneCarline && 'x mandatory'};
	padding: ${(props) => props.isMobile && props.theme.size.static250};
	max-width: 100%;
	height:  ${(props) => props.isMobile && props.hasOneCarline && 'fit-content'};
	

	@media screen and (max-width: 1199px) { 
		position:  ${(props) => props.isMobile && props.hasOneCarline && 'relative'};
		top:  ${(props) => props.isMobile && props.hasOneCarline && '0'};
		padding-bottom:  ${(props) => !props.isMobile && props.hasOneCarline && 'var(--size-dynamic0120)'};

		& > div {
			scroll-snap-align: center;
		}
	}

	@media screen and (max-width: 559px) {
		top: ${(props) => props.isMobile && !props.hasOneCarline && '120px'};
	}

	::-webkit-scrollbar {
		display: none; /* for Chrome, Safari and Opera */
	  	scrollbar-width: none;  /* Firefox */
	}
`;

export const OfertasCardContainer = ({
	carline,
	hasOneCarline,
	isMobile,
	isLayerVisible,
	setIsLayerVisible,
	selectedOferta,
	setSelectedOferta,
	handleLayerOnClick,
	setSelectedPromotion,
	setIsFinanceLayerVisible,
	setFinanceData,
	handleScrollToDisclaimer,
	isCallMeNowOpen,
}: {
	carline: CarlineWithOfertas;
	hasOneCarline: boolean;
	isMobile: boolean;
	isLayerVisible: boolean;
	setIsLayerVisible: React.Dispatch<React.SetStateAction<boolean>>;
	selectedOferta: Oferta;
	setSelectedOferta: React.Dispatch<React.SetStateAction<Oferta>>;
	className: string;
	handleLayerOnClick: () => void;
	setSelectedPromotion: React.Dispatch<
		React.SetStateAction<Promocion | undefined>
	>;
	setIsFinanceLayerVisible: React.Dispatch<React.SetStateAction<boolean>>;
	setFinanceData: React.Dispatch<React.SetStateAction<FinanceData>>;
	handleScrollToDisclaimer: (reference: string) => void;
	isCallMeNowOpen: boolean;
}): React.ReactElement => {
	const containerRef = useRef<HTMLDivElement>(null);

	const [visibleOfertaIndex, setVisibleOfertaIndex] = useState(0);
	const [hasScrolled, setHasScrolled] = useState(false);

	const trackingManager = useTrackingManager();

	const getTracking = useCallback(
		(carline: CarlineWithOfertas, oferta: Oferta) => {
			const pagoFinanciado = getPagoFinanciado(oferta.datosEconomicos);
			trackingManager.trackOfertasElementSwipe({
				CarlineId: carline.code.toString(),
				CarlineName: carline.title,
				SalesgroupName: oferta.carline?.salesgroup.title,
				ModelId: oferta?.configuracion.motorizacion.code.split('-')[0],
				ModelName: oferta.carline?.salesgroup.model.name,
				ModelYear: oferta.carline?.salesgroup.year.toString(),
				PriceRateType: pagoFinanciado
					? getFinanciacionType(pagoFinanciado)
					: undefined,
				PriceRateTotal: pagoFinanciado?.precio ?? undefined,
				PriceRateCurrency: 'EUR',
				PriceTotalTotal:
					getPagoUnico(oferta.datosEconomicos)?.precio ?? undefined,
				PriceTotalCurrency: 'EUR',
			});
		},
		[trackingManager],
	);

	useEffect(() => {
		const container = containerRef.current;

		if (!container) return;

		const handleScroll = () => {
			const cardWidth = container.scrollWidth / carline.ofertas.length;
			const scrollLeft = container.scrollLeft;

			const visibleCardIndex = Math.round(scrollLeft / cardWidth);
			setVisibleOfertaIndex(visibleCardIndex);

			if (visibleCardIndex === 0) {
				setHasScrolled(false);
			} else {
				setHasScrolled(true);
			}
		};

		container.addEventListener('scroll', handleScroll);

		return () => {
			container.removeEventListener('scroll', handleScroll);
		};
	}, [carline]);

	useEffect(() => {
		if (carline.ofertas.length > 1 && hasScrolled) {
			getTracking(carline, carline.ofertas[visibleOfertaIndex]);
		}
	}, [carline, getTracking, hasScrolled, visibleOfertaIndex]);

	return (
		<OfertasContainer
			className="ofertas__container"
			isMobile={isMobile}
			hasOneCarline={hasOneCarline}
			ref={containerRef}
		>
			<OfertaCard
				className="first"
				carline={carline}
				hasOneCarline={hasOneCarline}
				isMobile={isMobile}
				currentOferta={carline.ofertas[0]}
				isLayerVisible={isLayerVisible}
				setIsLayerVisible={setIsLayerVisible}
				selectedOferta={selectedOferta}
				setSelectedOferta={setSelectedOferta}
				handleLayerOnClick={handleLayerOnClick}
				setSelectedPromotion={setSelectedPromotion}
				setIsFinanceLayerVisible={setIsFinanceLayerVisible}
				setFinanceData={setFinanceData}
				visibleOfertaMobile={carline.ofertas[visibleOfertaIndex]}
				handleScrollToDisclaimer={handleScrollToDisclaimer}
				isCallMeNowOpen={isCallMeNowOpen}
			/>
			{carline.ofertas && carline.ofertas[1] && (
				<OfertaCard
					className="second"
					carline={carline}
					hasOneCarline={hasOneCarline}
					isMobile={isMobile}
					currentOferta={carline.ofertas[1]}
					isLayerVisible={isLayerVisible}
					setIsLayerVisible={setIsLayerVisible}
					selectedOferta={selectedOferta}
					setSelectedOferta={setSelectedOferta}
					handleLayerOnClick={handleLayerOnClick}
					setSelectedPromotion={setSelectedPromotion}
					setIsFinanceLayerVisible={setIsFinanceLayerVisible}
					setFinanceData={setFinanceData}
					visibleOfertaMobile={carline.ofertas[visibleOfertaIndex]}
					handleScrollToDisclaimer={handleScrollToDisclaimer}
					isCallMeNowOpen={isCallMeNowOpen}
				/>
			)}
		</OfertasContainer>
	);
};
